import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'wouter';
import {
  decodeHomeSearch,
  encodeHomeSearch,
} from '@/utils/homeSearchUrl';

const SEARCH_DEBOUNCE_MS = 400;

export interface UseHomeSearchParams {
  /** Immediate (un-debounced) search text — bind to the input. */
  searchTerm: string;
  /** Debounced search text — use for querying. */
  debouncedSearch: string;
  selectedTagsByGroup: Record<string, string[]>;
  selectedRatings: number[];
  setSearchTerm: (value: string) => void;
  toggleTag: (tag: string, group: string) => void;
  toggleRating: (value: number) => void;
  clearFilters: () => void;
}

/**
 * Drives Home's search/filter state through the URL query string so that
 * browser Back restores it. The URL is the source of truth:
 *   - state is initialised from the URL on mount (covers Back, which remounts
 *     Home, and shared/deep-linked URLs);
 *   - user edits are written back to the URL with `replace` (no history spam
 *     while typing — Back skips intermediate keystroke states);
 *   - when the query changes externally (e.g. the bottom-nav Home button
 *     navigates to bare `/` while Home stays mounted) the state adopts it.
 *
 * A single reconciliation effect handles both directions, so a state→URL write
 * can't race a URL→state read. The search debounce is managed here (rather than
 * via useDebounce) so adopting an external change can reset the debounced value
 * synchronously — otherwise a stale debounced value would immediately re-publish
 * the just-cleared query.
 */
export function useHomeSearchParams(): UseHomeSearchParams {
  const [location, navigate] = useLocation();
  const [searchParams] = useSearchParams();
  const searchString = searchParams.toString();

  // Initialise once from the URL present at mount.
  const initial = useMemo(() => decodeHomeSearch(searchParams), []); // eslint-disable-line react-hooks/exhaustive-deps

  const [searchTerm, setSearchTermState] = useState(initial.searchTerm);
  const [debouncedSearch, setDebouncedSearch] = useState(initial.searchTerm);
  const [selectedTagsByGroup, setSelectedTagsByGroup] = useState(initial.selectedTagsByGroup);
  const [selectedRatings, setSelectedRatings] = useState(initial.selectedRatings);

  const debounceTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const clearDebounce = () => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
  };
  useEffect(() => clearDebounce, []);

  const setSearchTerm = useCallback((value: string) => {
    setSearchTermState(value);
    clearDebounce();
    debounceTimer.current = setTimeout(() => setDebouncedSearch(value), SEARCH_DEBOUNCE_MS);
  }, []);

  // Reconcile state ↔ URL. `lastWritten` lets us recognise our own writes
  // echoing back as a searchString change (vs a genuine external navigation).
  const lastWritten = useRef<string | null>(null);
  const prevSearch = useRef(searchString);
  useEffect(() => {
    const external =
      searchString !== prevSearch.current && searchString !== lastWritten.current;

    if (external) {
      // The URL changed under us (Home button, Back/forward, deep link). Adopt
      // it wholesale and reset the debounced search synchronously so the write
      // branch below can't re-publish a stale value.
      const decoded = decodeHomeSearch(new URLSearchParams(searchString));
      clearDebounce();
      setSearchTermState(decoded.searchTerm);
      setDebouncedSearch(decoded.searchTerm);
      setSelectedTagsByGroup(decoded.selectedTagsByGroup);
      setSelectedRatings(decoded.selectedRatings);
      prevSearch.current = searchString;
      return;
    }

    const next = encodeHomeSearch({
      searchTerm: debouncedSearch,
      selectedTagsByGroup,
      selectedRatings,
    }).toString();
    if (next !== searchString) {
      lastWritten.current = next;
      // Navigate directly (rather than via setSearchParams) so an empty query
      // produces a clean path instead of a dangling "?".
      navigate(next ? `${location}?${next}` : location, { replace: true });
    }
    prevSearch.current = searchString;
  }, [searchString, debouncedSearch, selectedTagsByGroup, selectedRatings, location, navigate]);

  const toggleTag = useCallback((tag: string, group: string) => {
    setSelectedTagsByGroup((prev) => {
      const current = prev[group] ?? [];
      const next = current.includes(tag)
        ? current.filter((t) => t !== tag)
        : [...current, tag];
      if (next.length === 0) {
        const rest = { ...prev };
        delete rest[group];
        return rest;
      }
      return { ...prev, [group]: next };
    });
  }, []);

  const toggleRating = useCallback((value: number) => {
    setSelectedRatings((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  }, []);

  const clearFilters = useCallback(() => {
    clearDebounce();
    setSearchTermState('');
    setDebouncedSearch('');
    setSelectedTagsByGroup({});
    setSelectedRatings([]);
  }, []);

  return {
    searchTerm,
    debouncedSearch,
    selectedTagsByGroup,
    selectedRatings,
    setSearchTerm,
    toggleTag,
    toggleRating,
    clearFilters,
  };
}

